import React from "react";
import { omit } from "lodash";
import { get } from "react-hook-form";
import { TAG_DISPLAY } from "common/constants";
import { spacing } from "../../../../../../../../stitches.config";
import Label from "../../../../../../../Inputs/Label";
import { Checkbox } from "../../../../../../../Inputs/react-hook-form";
import {
  getUiSchemaPropertyFormPath,
  useBuilderFormContext,
} from "../../../form/form";
import { FieldWrapper } from "../../__styles__/SidebarColumn";
import {
  HelpText,
  WidgetSettingsContainer,
} from "../__styles__/WidgetSettings";
import type { InputSettingsFormProps } from "../FieldSettingsView";
import { DefaultValuesSection } from "../shared/DefaultValuesSection";
import { DisplaySettings } from "../shared/DisplaySettings";
import { FieldPermissionsConfig } from "../shared/FieldPermissionsConfig";
import { FieldSettingsConfig } from "../shared/FieldSettingsConfig";
import { HelperTextInput } from "../shared/HelperTextInput";
import { PlaceholderTextInput } from "../shared/PlaceholderTextInput";
import { RandomizeOptionOrderCheckbox } from "../shared/RandomizeOptionOrderCheckbox";
import { DropdownStaticDefaultInput } from "./DropdownStaticDefaultInput";
import { OptionsList } from "./OptionsList";
import type { DropdownInputType, DropdownOption, OneOfOptions } from "./types";
import { getSchemaOptionsPath, useSetOptions } from "./utils";

export type DropdownWidgetSettingsProps = InputSettingsFormProps & {
  inputType: DropdownInputType;
};

function assertValidInputType(
  props: InputSettingsFormProps,
): asserts props is DropdownWidgetSettingsProps {
  if (
    !props.inputType ||
    !["select", "multiSelect"].includes(props.inputType)
  ) {
    throw new Error(`Invalid input type: ${props.inputType}`);
  }
}

export function DropdownWidgetSettings(props: InputSettingsFormProps) {
  const { fieldPath, isModuleInput, isReadOnly } = props;

  assertValidInputType(props);

  return (
    <WidgetSettingsContainer>
      <FieldSettingsConfig
        fieldPath={fieldPath}
        isModuleInput={isModuleInput}
        isReadOnly={isReadOnly}
        isFormulaField={false}
      />
      <PlaceholderTextInput fieldPath={fieldPath} isReadOnly={isReadOnly} />
      <HelperTextInput fieldPath={fieldPath} isReadOnly={isReadOnly} />
      <div>
        <Label
          text="Dropdown options"
          required
          css={{ marginBottom: spacing.xs }}
        />
        <HelpText style={{ marginTop: 0, paddingBottom: spacing.s.value }}>
          Labels are visible to users, while values are backend identifiers and
          hidden from them.
        </HelpText>
      </div>
      <UseAsTagCheckbox {...props} />
      <RandomizeOptionOrderCheckbox
        fieldPath={fieldPath}
        isReadOnly={isReadOnly}
      />
      <OptionsList {...props} />
      <DefaultValuesSection>
        <FieldWrapper data-testid="static-default-value-wrapper">
          <DropdownStaticDefaultInput {...props} />
        </FieldWrapper>
      </DefaultValuesSection>
      <FieldPermissionsConfig fieldPath={fieldPath} isReadOnly={isReadOnly} />
      <DisplaySettings fieldPath={fieldPath} isReadOnly={isReadOnly} />
    </WidgetSettingsContainer>
  );
}

function UseAsTagCheckbox({
  fieldPath,
  isReadOnly,
  inputType,
}: DropdownWidgetSettingsProps) {
  const { control, getValues } = useBuilderFormContext();

  const useAsTagPropertyFormPath = getUiSchemaPropertyFormPath(
    fieldPath,
    "useAsTag",
  );

  const optionsPath = getSchemaOptionsPath(fieldPath, inputType);
  const setOptions = useSetOptions(fieldPath, inputType);

  return (
    <Checkbox
      control={control}
      name={useAsTagPropertyFormPath}
      label="Display selected label option on information panel"
      disabled={isReadOnly}
      additionalOnChange={e => {
        const options = get(getValues(), optionsPath) as OneOfOptions;

        if (!options) {
          return;
        }

        const newOptions = options.map(option => {
          // leave string options as-is because they are formatted as objects in
          // the options list
          if (typeof option === "string") {
            return option;
          }

          // checked == display as tag, so add a default display value
          if (e.target.checked) {
            return {
              display: TAG_DISPLAY.NEUTRAL,
              ...option,
            };
          } else {
            // unchecked == display as plain text, so remove the display
            // property
            return omit(option, "display") as DropdownOption;
          }
        }) as OneOfOptions;

        // update both schema and uiSchema because our form components expect
        // this to be duplicated across both
        setOptions(newOptions);
      }}
    />
  );
}
